import { SelectField } from '~/common/components';
import { Col, Icon, useConfiguredForm } from '@wyz/ui';
import { LocaleUtils } from '@wyz/utils';
import { Fragment } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useManageCriteria } from '~/common/api';
import { CriteriaHelpers } from '~/connect/helpers';
import {
  ICriteria,
  ICriteriaAwareForm,
  ICriteriaVisibilityFilter,
} from '~/connect/types';
import { useI18n, useLocale } from '~/common/context';

type CriteriaFieldProps = {
  associatedCriteriaIds: Array<ICriteria['id']>;
  onRemove: (idx: number) => void;
  name?: string;
  visibilityFilter?: ICriteriaVisibilityFilter;
};
const CriteriaField = ({
  associatedCriteriaIds,
  onRemove,
  name = 'criteria',
  visibilityFilter,
}: CriteriaFieldProps) => {
  const { locale } = useLocale();
  const { t } = useI18n();
  const methods = useFormContext<ICriteriaAwareForm>();
  const configurationContext = useConfiguredForm();
  const associatedCriteria = useWatch({
    control: methods.control,
    name: name as unknown as 'criteria',
  });
  const manageCriteria = useManageCriteria({
    getAll: {
      enabled: true,
      args: visibilityFilter,
      placeholderData: (previousData) => previousData,
    },
  });

  return (
    <>
      {associatedCriteriaIds.map((associatedCriterionId, criterionIdx) => {
        const fullCriterion = manageCriteria.getAll.data?.data.find(
          (item) => item.id === associatedCriterionId,
        );
        if (!fullCriterion) {
          return <Fragment key={criterionIdx} />;
        }
        const selectedValueIds =
          associatedCriteria?.[criterionIdx]?.values ?? [];
        // selected values hidden for the enseignes are kept, and flagged
        const staleValueIds = fullCriterion.values
          .filter(
            (value) => !value.available && selectedValueIds.includes(value.id),
          )
          .map((value) => value.id);

        return (
          <Col md={4} key={fullCriterion.id} className='position-relative'>
            <input
              type='hidden'
              {...methods.register(
                `${name}.${criterionIdx}.criterion.id` as unknown as keyof ICriteriaAwareForm,
              )}
              value={fullCriterion.id}
            />

            {configurationContext.mode !== 'READ_ONLY' && (
              <span
                className='position-absolute'
                style={{ right: 15, zIndex: 1 }}
              >
                <Icon
                  icon='trash'
                  width={'1.2rem'}
                  height={'1.2rem'}
                  role='button'
                  onClick={() => {
                    onRemove(criterionIdx);
                  }}
                />
              </span>
            )}
            <SelectField<true>
              label={fullCriterion.translatedName ?? fullCriterion.name}
              name={`${name}.${criterionIdx}.values`}
              options={CriteriaHelpers.sorterByName(
                fullCriterion.values.filter(
                  (value) =>
                    value.available || staleValueIds.includes(value.id),
                ),
              ).map((criterionValue) => ({
                label:
                  fullCriterion.isReserved && fullCriterion.code === 'COUNTRY'
                    ? (LocaleUtils.getHumanReadableLabel({
                        userLocale: locale,
                        locale: criterionValue.label,
                        fallback: criterionValue.label,
                        options: { type: 'region' },
                      }) as string)
                    : criterionValue.label,
                value: criterionValue.id,
              }))}
              warningText={
                staleValueIds.length > 0
                  ? t('app.criteria.value.not_available_warning')
                  : undefined
              }
              formGroupClassName=''
              isMulti
            />
          </Col>
        );
      })}
    </>
  );
};

export default CriteriaField;
